import { useEffect } from 'react';

import { InviteSegmentEvents } from '../constants';
import { useInviteTracking } from './useInviteTracking';

type AxiosErrorShape = {
  name?: string;
  response?: { status?: number };
  code?: string;
};

type UseTrackCoveragePricingResolvedProps = {
  propertyId: string | number | undefined;
  enabled: boolean;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  hasCustomScreeningStatusLabels: boolean;
  pricingOptionCount: number;
  // The resolved pricing data (raw `/coverages` rules or derived options).
  // Used as an identity token: the effect dep array dedupes on its reference,
  // so the event only fires once per resolved response and not during the
  // debounce gap before a refetch starts.
  pricingData: unknown;
};

// Fires `CoveragePricingResolved` once per resolved /coverages outcome.
// Re-fires when the response identity changes or the outcome flips.
export const useTrackCoveragePricingResolved = ({
  propertyId,
  enabled,
  isLoading,
  isError,
  error,
  hasCustomScreeningStatusLabels,
  pricingOptionCount,
  pricingData,
}: UseTrackCoveragePricingResolvedProps) => {
  const { trackInvite } = useInviteTracking();

  useEffect(() => {
    if (!propertyId || !enabled || isLoading) return;
    if (!isError && !pricingData) return;

    if (isError) {
      const axiosError = error as AxiosErrorShape | null | undefined;
      trackInvite(InviteSegmentEvents.CoveragePricingResolved, {
        property_id: propertyId,
        resolved_successfully: false,
        has_custom_screening_status_labels: hasCustomScreeningStatusLabels,
        has_dynamic_pricing_labels: false,
        pricing_option_count: 0,
        error_type: axiosError?.name ?? 'unknown',
        http_status: axiosError?.response?.status,
        error_code: axiosError?.code,
      });
      return;
    }

    trackInvite(InviteSegmentEvents.CoveragePricingResolved, {
      property_id: propertyId,
      resolved_successfully: true,
      has_custom_screening_status_labels: hasCustomScreeningStatusLabels,
      has_dynamic_pricing_labels: true,
      pricing_option_count: pricingOptionCount,
    });
  }, [
    propertyId,
    enabled,
    isLoading,
    isError,
    error,
    hasCustomScreeningStatusLabels,
    pricingOptionCount,
    pricingData,
    trackInvite,
  ]);
};
